/** Which of a report's lines the filing page states, and how each reads - pure, so it is tested without
 * a DOM. See `StatedBand.tsx` for why these are the report's own lines and not a list kept here. */

import type { ReportLine } from "../../api/queries";
import { intensity } from "../../lib/format";
import { amount } from "../reportsFormat";

/** A disclosure EmitIQ does not produce at all (SB 253's Scope 3, SB 261's report). The sentence is
 * the server's and is compared as the Disclosures view compares it. */
export function isUnsupported(line: ReportLine): boolean {
  return line.unavailable_because === "EmitIQ does not produce this disclosure. See the limitation.";
}

/** The lines that state a quantity, plus any line the product cannot produce at all. Narrative and
 * comparative lines are the Disclosures view's. */
export function statedLines(lines: readonly ReportLine[]): ReportLine[] {
  const figure = new Set(["emissions", "energy", "intensity"]);
  return lines
    .filter((line) => figure.has(line.measure) || (line.measure === "narrative" && isUnsupported(line)))
    .sort((a, b) => a.ordinal - b.ordinal);
}

/** The unit as a reader writes it: the formula's subscript, and nothing else changed. */
function unitText(unit: string | null): string {
  return (unit ?? "").replace("CO2e", "CO₂e");
}

export function statedValue(line: ReportLine): { text: string; unit: string; kind: "figure" | "word" } {
  if (isUnsupported(line)) return { text: "Not assessed", unit: "", kind: "word" };
  if (line.value === null) {
    return { text: line.measure === "intensity" ? "Not stated" : "No figure", unit: "", kind: "word" };
  }
  // An intensity is a small ratio; a tenth would print 0.1 for 0.116 (three significant figures).
  const text = line.measure === "intensity" ? intensity(line.value) : amount(line.value, line.unit);
  return { text, unit: unitText(line.unit), kind: "figure" };
}
